import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetRegistrationSummaryQuery as GetRegistrationSummary,
  GetTicketNumberSequencesForEventQuery as GetTicketNumberSequencesForEvent,
  Permission,
} from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditTicketNumberMutation from './EditTicketNumberMutation';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import GetTicketNumberSequencesForEventQuery from '../../TicketNumbers/GetTicketNumberSequencesForEventQuery';
import ProtectedSection from '../../../Common/ProtectedSection';
import RegistrationInfo from './RegistrationInfo';
import UI from '../../../../common/UI';
import useIntegerInput from '../../../../common/useIntegerInput';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

type Registration = GetRegistrationSummary['registrationSummary']['registrations'][0];
type TicketNumberSequence = GetTicketNumberSequencesForEvent['project']['event']['ticket_number_sequences'][0];

export interface EditTicketNumberFormProps {
  registration: Registration;
  sequences?: TicketNumberSequence[];
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const EditTicketNumberForm = ({
  registration, sequences, onSuccess, onCancel,
  refetchQueries = [GetParticipantDataQuery, GetRegistrationSummaryQuery],
}: EditTicketNumberFormProps,
) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const integerInput = useIntegerInput();
  const project = useProject();

  const getValues = (registration: Registration) => (registration.ticket_number_locked ? {
    ticket_number_sequence_id: registration.ticket_number_sequence?.id || 'none',
    ticket_number: registration.ticket_number_digits,
  } : {
    ticket_number_sequence_id: registration.ticket_number_sequence ? 'auto' : 'none',
    ticket_number: null,
  });

  const form = useForm({
    values: getValues(registration),
  });

  const [cannotAssign, setCannotAssign] = useState(false);

  const [editPurchase, { error, loading }] = useMutation(
    EditTicketNumberMutation,
    {
      variables: {
        input: {
          id: registration.purchase.id,
          ticket_number: {
            auto_assign: form.values.ticket_number_sequence_id === 'auto',
            ...!['none', 'auto'].includes(form.values.ticket_number_sequence_id) && {
              ticket_number_sequence: { id: form.values.ticket_number_sequence_id },
              ticket_number: form.values.ticket_number,
            },
          },
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: ({ editPurchase }, { variables }) => {
        if (variables.input.ticket_number.auto_assign && !editPurchase.ticket_number) {
          setCannotAssign(true);
        } else {
          onSuccess?.();
          setCannotAssign(false);
          notifier.success(t('notifications.ticket_number_edited'));
        }
      },
    },
  );

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const allowEnterDigits = !['none', 'auto'].includes(form.values.ticket_number_sequence_id);

  const selectedSequence = sequences?.filter(
    (sequence) => sequence.id === form.values.ticket_number_sequence_id,
  )[0];

  return (
    <UI.Form onSubmit={() => editPurchase()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('edit_ticket_number')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('edit_ticket_number_description')}
          </UI.Paragraph>
        </UI.Div>

        <RegistrationInfo registration={registration} />

        <UI.ServerErrors error={error} />

        {cannotAssign && (
          <UI.Div>
            <UI.Warning>
              {t('ticket_number_not_assigned_description')}
              <ProtectedSection permissions={[Permission.participant_logistics]}>
                {' '}
                <UI.Link to={`/${project.id}/participants/bib-numbers?event=${registration.event.id}`}>
                  {t('manage_start_groups')}
                </UI.Link>
              </ProtectedSection>
            </UI.Warning>
          </UI.Div>
        )}

        {!sequences && <UI.PageLoader />}

        {sequences && (
          <>
            <UI.InputGroup
              sc={{
                valid: !errors.ticket_number_sequence_id,
                touched: form.touched.ticket_number_sequence_id,
              }}
            >
              <UI.InputLabel htmlFor="TicketNumberSequence">
                {t('ticket_number_sequence')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.Select
                {...form.select('ticket_number_sequence_id')}
                id="TicketNumberSequence"
              >
                <option value="none">
                  {t('none')}
                </option>
                <option value="auto">
                  {t('assign_automatically')}
                </option>
                {sequences.map((sequence, index) => (
                  <option value={sequence.id} key={index}>{sequence.title}</option>
                ))}
              </UI.Select>
              <UI.ErrorMessages
                attribute={t('ticket_number_sequence')}
                errors={errors.ticket_number_sequence_id}
              />
            </UI.InputGroup>

            {allowEnterDigits && (
              <UI.InputGroup
                sc={{ valid: !errors.ticket_number, touched: form.touched.ticket_number }}
              >
                <UI.InputLabel htmlFor="TicketNumber">
                  {t('ticket_number_digits')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('assign_manually_description')}
                </UI.InputDescription>
                <UI.InputWrapper>
                  <UI.DebouncedInput
                    {...form.text('ticket_number', {
                      inputHandler: integerInput(),
                    })}
                    format={integerInput().format}
                    maxLength={8}
                    inputMode="numeric"
                    match={/[\d]/g}
                    id="TicketNumber"
                  />
                  {selectedSequence && form.values.ticket_number !== null && (
                    <UI.Span>
                      <UI.Label>
                        {selectedSequence.prefix}
                        {form.values.ticket_number}
                        {selectedSequence.suffix}
                      </UI.Label>
                    </UI.Span>
                  )}
                </UI.InputWrapper>
                <UI.ErrorMessages
                  attribute={t('ticket_number')}
                  errors={errors.ticket_number}
                />
              </UI.InputGroup>
            )}
          </>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('confirm')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

const schema = yup.object().shape({
  ticket_number: yup.string().nullable().when(['ticket_number_sequence_id'], {
    is: (value: string) => !['none', 'auto'].includes(value),
    then: yup.string().required(),
  }),
});

interface UseEditTicketNumberFormProps {
  registration: Registration;
  open?: boolean;
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

const useEditTicketNumberModal = ({
  registration, onSuccess,
  refetchQueries = [],
}: UseEditTicketNumberFormProps) => {
  const project = useProject();

  const [open, setOpen] = useState(false);

  const { data } = useQuery(
    GetTicketNumberSequencesForEventQuery,
    {
      variables: {
        project_id: project.id,
        event_id: registration.event.id,
      },
      skip: !open,
    },
  );

  const sequences = data?.project.event.ticket_number_sequences;

  const [doOpenModal, closeModal] = useModal(() => {
    const handleSuccess = () => {
      closeModal();
      onSuccess?.();
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        {!sequences && <UI.PageLoader />}

        {sequences && (
          <EditTicketNumberForm
            registration={registration}
            sequences={sequences}
            onSuccess={handleSuccess}
            onCancel={closeModal}
            refetchQueries={refetchQueries}
          />
        )}
      </UI.Modal>
    );
  }, [registration, sequences, refetchQueries]);

  const openModal = () => {
    setOpen(true);
    doOpenModal();
  };

  return [openModal, closeModal];
};

export default useEditTicketNumberModal;
